import { Menu, Download, Upload, Image, Play, Trash2, Copy, HelpCircle, BookOpen, Sparkles, Undo2, Redo2, ListTree, Lightbulb, Heart, LogIn, LogOut, Cloud, Share2, Shield, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EngiCoroLogo } from '@/components/workflow/EngiCoroLogo';
import { ConnectionStatusIndicator } from '@/components/workflow/ConnectionStatusIndicator';
import { ThemeToggle } from '@/components/workflow/ThemeToggle';
import { LanguageSelector } from '@/components/workflow/LanguageSelector';
import { useTranslation } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { IS_WEB } from '@/lib/buildMode';
import type { ConnectionDebugInfo } from '@/hooks/useDocuments';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useState } from 'react';
import { BRAND_NAVY, BRAND_NAVY_VAR, BRAND_SIDEBAR_VAR } from '@/constants/brandColors';

interface MobileTopBarProps {
  onSave: () => void;
  onLoad: () => void;
  onExportPNG: () => void;
  onSimulate: () => void;
  onClear: () => void;
  onImportJson: () => void;
  onCopyJson: () => void;
  onHelp: () => void;
  onAiJson: () => void;
  onAiAssistant: () => void;
  onPreview: () => void;
  onLogin?: () => void;
  onCloudWorkspace?: () => void;
  onShareWorkspace?: () => void;
  onFeatureRequest?: () => void;
  onBugReport?: () => void;
  onDonate?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  isConnected?: boolean;
  connectionDebugInfo?: ConnectionDebugInfo;
  isDirty?: boolean;
  /** Controlled drawer-state — sätt via parent för att låta gester
   *  (edge-swipe från top) öppna menyn utan att behöva refar:a knappen. */
  drawerOpen?: boolean;
  onDrawerOpenChange?: (open: boolean) => void;
}

export function MobileTopBar({
  onSave, onLoad, onExportPNG, onSimulate, onClear, onImportJson, onCopyJson,
  onHelp, onAiJson, onAiAssistant, onPreview, onLogin, onCloudWorkspace,
  onShareWorkspace, onFeatureRequest, onBugReport, onDonate, onUndo, onRedo, canUndo, canRedo,
  isConnected, connectionDebugInfo, isDirty,
  drawerOpen: drawerOpenProp, onDrawerOpenChange,
}: MobileTopBarProps) {
  const { t } = useTranslation();
  const { user, isAuthenticated, signOut } = useAuth();
  const { isAdmin } = useAdminAuth();
  // Stöd både controlled (drawerOpenProp + onDrawerOpenChange) och
  // uncontrolled (intern useState) — om parent bara skickar onDrawerOpenChange
  // utan drawerOpenProp blir det effektivt uncontrolled.
  const [drawerOpenInternal, setDrawerOpenInternal] = useState(false);
  const drawerOpen = drawerOpenProp ?? drawerOpenInternal;
  const setDrawerOpen = (v: boolean) => {
    onDrawerOpenChange?.(v);
    if (drawerOpenProp === undefined) setDrawerOpenInternal(v);
  };

  const menuItem = (icon: React.ReactNode, label: string, onClick?: () => void, disabled?: boolean, destructive?: boolean) => (
    <button
      onClick={() => { onClick?.(); setDrawerOpen(false); }}
      disabled={disabled}
      className={`flex items-center gap-3 w-full px-4 py-3 text-left transition-colors active:bg-muted/80 disabled:opacity-40 ${destructive ? 'text-destructive' : ''}`}
      style={{ minHeight: '48px' }}
    >
      {icon}
      <span className="text-sm font-medium">{label}</span>
    </button>
  );

  const divider = <div className="h-px bg-border mx-4 my-1" />;

  return (
    <>
      <header className="h-12 flex items-center justify-between px-3 shadow-md flex-shrink-0 relative z-30" style={{ background: BRAND_SIDEBAR_VAR, paddingTop: 'env(safe-area-inset-top, 0px)', minHeight: 'calc(48px + env(safe-area-inset-top, 0px))' }} role="banner" data-testid="mobile-topbar">
        <div className="flex items-center gap-2 min-w-0">
          <EngiCoroLogo className="h-8 flex-shrink-0" />
          <div className="flex items-center gap-1.5">
            <ConnectionStatusIndicator isConnected={!!isConnected} debugInfo={connectionDebugInfo} />
            {isDirty && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={onUndo} disabled={!canUndo} className="h-9 w-9 disabled:opacity-40" style={{ color: BRAND_NAVY_VAR }} aria-label={t('undo')}>
            <Undo2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onRedo} disabled={!canRedo} className="h-9 w-9 disabled:opacity-40" style={{ color: BRAND_NAVY_VAR }} aria-label={t('redo')}>
            <Redo2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setDrawerOpen(true)} className="h-9 w-9" style={{ color: BRAND_NAVY_VAR }} aria-label={t('openMenu')}>
            <Menu className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="p-0 w-[280px]">
          <SheetTitle className="sr-only">{t('menu')}</SheetTitle>
          <div className="overflow-y-auto h-full min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
            {/* User section */}
            <div className="px-4 py-3 border-b border-border bg-muted/30">
              {isAuthenticated ? (
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: BRAND_NAVY }}>
                    {(user?.email ?? '').split('@')[0].slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{user?.email}</p>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">{t('notSignedIn')}</p>
              )}
            </div>

            <div className="py-2">
              {menuItem(<HelpCircle className="w-5 h-5 flex-shrink-0" />, t('help'), onHelp)}
              {isAuthenticated && (
                <>
                  {menuItem(<BookOpen className="w-5 h-5 flex-shrink-0" />, t('aiAndJson'), onAiJson)}
                  {menuItem(<Sparkles className="w-5 h-5 flex-shrink-0" />, t('aiAssistant'), onAiAssistant)}
                </>
              )}

              {divider}

              {menuItem(<Download className="w-5 h-5 flex-shrink-0" />, t('saveFile'), onSave)}
              {menuItem(<Upload className="w-5 h-5 flex-shrink-0" />, t('loadFile'), onLoad)}
              {isAuthenticated && (
                <>
                  {menuItem(<Image className="w-5 h-5 flex-shrink-0" />, t('exportPng'), onExportPNG)}
                  {menuItem(<Cloud className="w-5 h-5 flex-shrink-0" />, t('cloudWorkspaces'), onCloudWorkspace)}
                  {menuItem(<Share2 className="w-5 h-5 flex-shrink-0" />, t('shareWorkspace'), onShareWorkspace)}
                </>
              )}

              {divider}

              {menuItem(<Upload className="w-5 h-5 flex-shrink-0" />, t('import_'), onImportJson)}
              {menuItem(<Copy className="w-5 h-5 flex-shrink-0" />, t('copyJson'), onCopyJson)}
              {isAuthenticated && (
                <>
                  {menuItem(<Play className="w-5 h-5 flex-shrink-0" />, t('simulate'), onSimulate)}
                  {menuItem(<ListTree className="w-5 h-5 flex-shrink-0" />, t('overview'), onPreview)}
                </>
              )}

              {IS_WEB && (
                <>
                  {divider}
                  {menuItem(<Bug className="w-5 h-5 flex-shrink-0" />, t('reportBug'), onBugReport)}
                  {menuItem(<Lightbulb className="w-5 h-5 flex-shrink-0" />, t('featureRequest'), onFeatureRequest)}
                  {menuItem(<Heart className="w-5 h-5 flex-shrink-0 text-pink-500" />, t('donate'), onDonate)}
                </>
              )}

              {divider}

              <div className="flex items-center gap-2 px-4 py-2">
                <ThemeToggle className="text-foreground hover:bg-muted/50" />
                <LanguageSelector className="text-foreground hover:bg-muted/50" />
              </div>

              {divider}

              {isAdmin && menuItem(<Shield className="w-5 h-5 flex-shrink-0" />, t('adminDashboard'), () => window.location.href = '/admin')}

              {isAuthenticated ? (
                menuItem(<LogOut className="w-5 h-5 flex-shrink-0" />, t('signOut'), signOut, false, true)
              ) : (
                menuItem(<LogIn className="w-5 h-5 flex-shrink-0" />, t('signIn'), onLogin)
              )}

              {divider}

              {menuItem(<Trash2 className="w-5 h-5 flex-shrink-0" />, t('clear'), onClear, false, true)}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
